import { useCallback, useEffect, useState } from "react";
import { Spinner } from "@chakra-ui/react";
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiX, FiAlertCircle } from "react-icons/fi";
import { Button } from "newui/components/Button";
import SkeletonBase from "newui/components/Skeleton";
import { useEvaluationCriteria } from "../../../hooks/useEvaluationCriteria";
import type {
	EvaluationCriterion,
	CreateCriterionPayload,
} from "../../../types/criteria.type";
import { EVALUATION_TYPE_OPTIONS } from "../../../types/criteria.type";
import { CriteriaSearchSection } from "./components/CriteriaSearchSection";
import { showToast } from "utils/helpers";

interface EvaluationCriteriaStepProps {
	rfxId: number;
	rfxSubmissionId?: number;
	isLoading?: boolean;
	isRfxLead?: boolean;
	isLocked?: boolean;
	rfxLeadName?: string;
	rfxLeadEmail?: string;
	onBeforeNext?: (check: () => Promise<boolean>) => void;
}

interface CriterionFormValues {
	name: string;
	description: string;
	rfx_evaluation_type_id: number;
	weight: string;
}

const EMPTY_FORM: CriterionFormValues = {
	name: "",
	description: "",
	rfx_evaluation_type_id: 2,
	weight: "",
};

const getWeightColorClass = (totalWeight: number): string => {
	if (totalWeight === 100) {
		return "text-green-600";
	}
	if (totalWeight > 100) {
		return "text-red-600";
	}
	if (totalWeight > 0) {
		return "text-amber-600";
	}
	return "text-gray-400";
};

export const EvaluationCriteriaStep: React.FC<EvaluationCriteriaStepProps> = ({
	rfxId,
	isLoading: externalLoading = false,
	isRfxLead = false,
	isLocked = false,
	rfxLeadName,
	rfxLeadEmail,
	onBeforeNext,
}) => {
	const canEdit = isRfxLead && !isLocked;
	const {
		criteria,
		error,
		isLoading,
		isSaving,
		totalWeight,
		isWeightValid,
		fetchCriteria,
		addCriterion,
		editCriterion,
		removeCriterion,
	} = useEvaluationCriteria({ rfxId });

	const [isAdding, setIsAdding] = useState(false);
	const [editingId, setEditingId] = useState<number | null>(null);
	const [formValues, setFormValues] = useState<CriterionFormValues>(EMPTY_FORM);
	const [formError, setFormError] = useState<string | null>(null);

	const checkBeforeNext = useCallback(async (): Promise<boolean> => {
		if (!isWeightValid) {
			showToast(
				"Total criteria weight must equal 100% before proceeding.",
				"Invalid Weight",
				false
			);
			return false;
		}
		return true;
	}, [isWeightValid]);

	useEffect(() => {
		if (onBeforeNext) {
			onBeforeNext(checkBeforeNext);
		}
	}, [onBeforeNext, checkBeforeNext]);

	useEffect(() => {
		fetchCriteria();
	}, [fetchCriteria]);

	const validateForm = (): boolean => {
		if (!formValues.name.trim()) {
			setFormError("Name is required");
			return false;
		}
		const weight = Number(formValues.weight);
		if (isNaN(weight) || weight < 0 || weight > 100) {
			setFormError("Weight must be between 0 and 100");
			return false;
		}
		setFormError(null);
		return true;
	};

	const handleStartAdd = (): void => {
		setIsAdding(true);
		setEditingId(null);
		setFormValues(EMPTY_FORM);
		setFormError(null);
	};

	const handleStartEdit = (criterion: EvaluationCriterion): void => {
		setEditingId(criterion.id);
		setIsAdding(false);
		setFormValues({
			name: criterion.name,
			description: criterion.description ?? "",
			rfx_evaluation_type_id: criterion.rfx_evaluation_type_id,
			weight: String(criterion.weight),
		});
		setFormError(null);
	};

	const handleCancel = (): void => {
		setIsAdding(false);
		setEditingId(null);
		setFormValues(EMPTY_FORM);
		setFormError(null);
	};

	const handleSaveNew = async (): Promise<void> => {
		if (!validateForm()) {
			return;
		}
		const payload: CreateCriterionPayload = {
			name: formValues.name.trim(),
			description: formValues.description.trim() || undefined,
			rfx_evaluation_type_id: formValues.rfx_evaluation_type_id,
			weight: Number(formValues.weight),
		};
		const result = await addCriterion(payload);
		if (result) {
			setIsAdding(false);
			setFormValues(EMPTY_FORM);
		}
	};

	const handleSaveEdit = async (): Promise<void> => {
		if (editingId === null || !validateForm()) {
			return;
		}
		const payload = {
			id: editingId,
			name: formValues.name.trim(),
			description: formValues.description.trim() || undefined,
			rfx_evaluation_type_id: formValues.rfx_evaluation_type_id,
			weight: Number(formValues.weight),
		};
		const result = await editCriterion(editingId, payload);
		if (result) {
			setEditingId(null);
			setFormValues(EMPTY_FORM);
		}
	};

	const handleDelete = async (criterionId: number): Promise<void> => {
		await removeCriterion(criterionId);
	};

	const updateField = (field: keyof CriterionFormValues, value: string): void => {
		setFormValues((prev) => ({ ...prev, [field]: value }));
		if (formError) {
			setFormError(null);
		}
	};

	if (externalLoading || isLoading) {
		return (
			<div className="space-y-3">
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
			</div>
		);
	}

	return (
		<div data-testid="evaluation-criteria-step">
			{error && (
				<div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
					<FiAlertCircle className="shrink-0" />
					<span>{error}</span>
				</div>
			)}

			{/* Criteria table */}
			{criteria.length > 0 && (
				<div className="mb-4 overflow-x-auto">
					<table className="w-full text-sm" data-testid="criteria-table">
						<thead>
							<tr className="border-b border-gray-200 text-left text-gray-500">
								<th className="pb-2 pr-4 font-medium">Name</th>
								<th className="pb-2 pr-4 font-medium">Description</th>
								<th className="pb-2 pr-4 font-medium">Type</th>
								<th className="pb-2 pr-4 font-medium text-right">Weight (%)</th>
								{canEdit && <th className="pb-2 font-medium text-right">Actions</th>}
							</tr>
						</thead>
						<tbody>
							{criteria.map((criterion) => (
								<tr
									key={criterion.id}
									className="border-b border-gray-100"
									data-testid={`criterion-row-${criterion.id}`}
								>
									{canEdit && editingId === criterion.id ? (
										<>
											<td className="py-2 pr-4">
												<input
													type="text"
													value={formValues.name}
													onChange={(e) => updateField("name", e.target.value)}
													className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
													data-testid="edit-name-input"
												/>
											</td>
											<td className="py-2 pr-4">
												<input
													type="text"
													value={formValues.description}
													onChange={(e) => updateField("description", e.target.value)}
													className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
													data-testid="edit-description-input"
												/>
											</td>
											<td className="py-2 pr-4">
												<select
													value={formValues.rfx_evaluation_type_id}
													onChange={(e) => setFormValues((prev) => ({ ...prev, rfx_evaluation_type_id: Number(e.target.value) }))}
													className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
													data-testid="edit-type-select"
												>
													{EVALUATION_TYPE_OPTIONS.map((opt) => (
														<option key={opt.value} value={opt.value}>
															{opt.label}
														</option>
													))}
												</select>
											</td>
											<td className="py-2 pr-4 text-right">
												<input
													type="number"
													min="0"
													max="100"
													value={formValues.weight}
													onChange={(e) => updateField("weight", e.target.value)}
													className="w-20 rounded border border-gray-300 px-2 py-1 text-right text-sm focus:border-blue-500 focus:outline-none"
													data-testid="edit-weight-input"
												/>
											</td>
											<td className="py-2 text-right">
												<div className="flex items-center justify-end gap-1">
													<button
														onClick={handleSaveEdit}
														disabled={isSaving}
														className="rounded p-1 text-green-600 hover:bg-green-50 disabled:opacity-50"
														data-testid="save-edit-btn"
													>
														{isSaving ? <Spinner size="xs" /> : <FiCheck size={16} />}
													</button>
													<button
														onClick={handleCancel}
														className="rounded p-1 text-gray-400 hover:bg-gray-100"
														data-testid="cancel-edit-btn"
													>
														<FiX size={16} />
													</button>
												</div>
											</td>
										</>
									) : (
										<>
											<td className="py-2 pr-4 font-medium text-gray-900">
												{criterion.name}
											</td>
											<td className="py-2 pr-4 text-gray-500">
												{criterion.description || "-"}
											</td>
											<td className="py-2 pr-4 text-gray-500">
												{EVALUATION_TYPE_OPTIONS.find(
													(o) => o.value === criterion.rfx_evaluation_type_id
												)?.label ?? "Unknown"}
											</td>
											<td className="py-2 pr-4 text-right text-gray-900">
												{criterion.weight}%
											</td>
											{canEdit && (
												<td className="py-2 text-right">
													<div className="flex items-center justify-end gap-1">
														<button
															onClick={() => handleStartEdit(criterion)}
															className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
															data-testid={`edit-btn-${criterion.id}`}
														>
															<FiEdit2 size={14} />
														</button>
														<button
															onClick={() => handleDelete(criterion.id)}
															disabled={isSaving}
															className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
															data-testid={`delete-btn-${criterion.id}`}
														>
															<FiTrash2 size={14} />
														</button>
													</div>
												</td>
											)}
										</>
									)}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{/* Non-lead info */}
			{!canEdit && !isRfxLead && criteria.length > 0 && (
				<div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700" data-testid="criteria-readonly-info">
					<FiAlertCircle className="shrink-0" size={16} />
					<span>
						Only the RFx Lead can update Criteria.
						{rfxLeadName && (
							<span className="ml-1 font-medium">{rfxLeadName}{rfxLeadEmail && ` (${rfxLeadEmail})`}</span>
						)}
					</span>
				</div>
			)}

			{/* Add new criterion form row */}
			{canEdit && isAdding && (
				<div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4" data-testid="add-criterion-form">
					{formError && (
						<div className="mb-3 text-sm text-red-600">{formError}</div>
					)}
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
						<div>
							<label className="mb-1 block text-xs font-medium text-gray-700">
								Name <span className="text-red-500">*</span>
							</label>
							<input
								type="text"
								value={formValues.name}
								onChange={(e) => updateField("name", e.target.value)}
								placeholder="e.g. Technical Capability"
								className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
								data-testid="add-name-input"
							/>
						</div>
						<div>
							<label className="mb-1 block text-xs font-medium text-gray-700">
								Description
							</label>
							<textarea
								value={formValues.description}
								onChange={(e) => updateField("description", e.target.value)}
								placeholder="Optional description"
								rows={1}
								className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
								data-testid="add-description-input"
							/>
						</div>
						<div>
							<label className="mb-1 block text-xs font-medium text-gray-700">
								Evaluation Type
							</label>
							<select
								value={formValues.rfx_evaluation_type_id}
								onChange={(e) => setFormValues((prev) => ({ ...prev, rfx_evaluation_type_id: Number(e.target.value) }))}
								className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
								data-testid="add-type-select"
							>
								{EVALUATION_TYPE_OPTIONS.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</select>
						</div>
						<div>
							<label className="mb-1 block text-xs font-medium text-gray-700">
								Weight (%)
							</label>
							<input
								type="number"
								min="0"
								max="100"
								value={formValues.weight}
								onChange={(e) => updateField("weight", e.target.value)}
								placeholder="0"
								className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
								data-testid="add-weight-input"
							/>
						</div>
					</div>
					<div className="mt-3 flex items-center gap-2">
						<Button
							btntype="primary"
							onClick={handleSaveNew}
							disabled={isSaving}
							data-testid="save-new-btn"
						>
							{isSaving ? <Spinner size="xs" /> : "Save"}
						</Button>
						<Button
							btntype="secondary"
							onClick={handleCancel}
							data-testid="cancel-new-btn"
						>
							Cancel
						</Button>
					</div>
				</div>
			)}

			{/* Weight summary and add button */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-3">
					{canEdit && !isAdding && (
						<Button
							btntype="secondary"
							onClick={handleStartAdd}
							disabled={editingId !== null}
							data-testid="add-criterion-btn"
						>
							<FiPlus className="mr-1 inline" size={14} />
							Add Criterion
						</Button>
					)}
				</div>
				{criteria.length > 0 && (
					<div
						className={`text-sm font-medium ${getWeightColorClass(totalWeight)}`}
						data-testid="weight-summary"
					>
						Total weight: {totalWeight}%
						{!isWeightValid && (
							<span className="ml-1 text-xs">(must equal 100%)</span>
						)}
					</div>
				)}
			</div>

			{/* Search and copy from previous RFx */}
			{canEdit && <CriteriaSearchSection rfxId={rfxId} onCopied={fetchCriteria} />}
		</div>
	);
};
